import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { RowData, Table } from '@tanstack/react-table'
import { ArrowRight, ArrowLeft, ArrowLeftToLine, ArrowRightFromLine } from 'lucide-react'
import React from 'react'

type PaginationControlsProps<TRowData> = Readonly<{
  isTableLoading: boolean
  selectedPageIndex: number
  table: Table<TRowData>
  pageSizeOptions?: number[]
}>

export const PAGE_SIZES = {
  TEN: 10,
  TWENTY: 20,
  THIRTY: 30,
}

export const DEFAULT_PAGINATION_OPTIONS = {
  defaultPageSize: PAGE_SIZES.TEN,
  pageSizes: Object.values(PAGE_SIZES),
}

export function PaginationControls<TRowData extends RowData>({
  isTableLoading,
  selectedPageIndex,
  table,
  pageSizeOptions = DEFAULT_PAGINATION_OPTIONS.pageSizes,
}: PaginationControlsProps<TRowData>) {
  const maxPageIndex = table.getPageCount() - 1
  const pageSize = table.getState().pagination.pageSize

  const visiblePageOptions = React.useMemo(() => {
    const totalPages = maxPageIndex + 1
    const pages: (number | '...')[] = []

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i)
    } else {
      if (selectedPageIndex <= 3) {
        pages.push(0, 1, 2, 3, '...', maxPageIndex)
      } else if (selectedPageIndex >= totalPages - 4) {
        pages.push(0, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1)
      } else {
        pages.push(
          0,
          '...',
          selectedPageIndex - 1,
          selectedPageIndex,
          selectedPageIndex + 1,
          '...',
          maxPageIndex,
        )
      }
    }

    return pages
  }, [selectedPageIndex, maxPageIndex])

  return (
    <div className='flex w-full items-center justify-between gap-2'>
      <div className='flex items-center gap-2'>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => {
            table.setPageSize(Number(value))
            table.setPageIndex(0)
          }}
        >
          <SelectTrigger className='bg-background w-32 rounded-md border border-neutral-75 px-3 py-2 text-xs font-normal'>
            {pageSize} items
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((opt) => (
              <SelectItem key={opt} value={opt.toString()}>
                {opt} items
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label htmlFor='page-size' className='text-sm text-neutral-500'>
          per page
        </label>
      </div>

      {maxPageIndex > 0 && (
        <div>
          <div className='flex h-6 w-full items-center justify-center gap-2'>
            <Button
              unstyled
              disabled={!table.getCanPreviousPage()}
              className={cn('text-neutral-150', !table.getCanPreviousPage() && 'opacity-40')}
              title='Go to first page'
              onClick={table.firstPage}
            >
              <ArrowLeftToLine className='h-5 w-5' />
            </Button>
            <Button
              unstyled
              disabled={!table.getCanPreviousPage()}
              className={cn('text-neutral-150', !table.getCanPreviousPage() && 'opacity-40')}
              title='Go to previous page'
              onClick={table.previousPage}
            >
              <ArrowLeft className='h-5 w-5' />
            </Button>

            {visiblePageOptions.map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className='px-2'>
                  ...
                </span>
              ) : (
                <PageNumberButton
                  key={`pagination-${page}`}
                  pageIndex={page}
                  selectedIndex={selectedPageIndex}
                  isFetching={isTableLoading}
                  onPageClick={table.setPageIndex}
                />
              ),
            )}

            <Button
              unstyled
              disabled={!table.getCanNextPage()}
              className={cn('text-neutral-150', !table.getCanNextPage() && 'opacity-40')}
              title='Go to next page'
              onClick={table.nextPage}
            >
              <ArrowRight className='h-5 w-5' />
            </Button>
            <Button
              unstyled
              disabled={!table.getCanNextPage()}
              className={cn('text-neutral-150', !table.getCanNextPage() && 'opacity-40')}
              title='Go to last page'
              onClick={table.lastPage}
            >
              <ArrowRightFromLine className='h-5 w-5' />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

type PageNumberButtonProps = Readonly<{
  pageIndex: number
  selectedIndex: number
  isFetching: boolean
  onPageClick: (pageIndex: number) => void
}>

function PageNumberButton({
  pageIndex,
  selectedIndex,
  isFetching,
  onPageClick,
}: PageNumberButtonProps) {
  const isSelected = pageIndex === selectedIndex
  return (
    <Button
      unstyled
      className='size-6 select-none'
      title={isSelected ? 'Current page number' : `Go to page ${pageIndex + 1}`}
      onClick={() => onPageClick(pageIndex)}
    >
      {isFetching && isSelected ? (
        <Spinner size='xs' color='black' />
      ) : (
        <div
          className={cn(
            'flex items-center justify-center rounded px-2 font-light text-neutral-150',
            isSelected && 'font-bold text-neutral-800',
          )}
        >
          {pageIndex + 1}
        </div>
      )}
    </Button>
  )
}
